import type { ActivityOverviewHealth } from '@sparkapp/shared-types';
import { cn } from '@/lib/utils';

const HEALTH_ITEMS: Array<{
  key: Exclude<keyof ActivityOverviewHealth, 'total'>;
  label: string;
  hint: string;
}> = [
  { key: 'noImage', label: 'No image', hint: 'Activities without a card image' },
  { key: 'noPreference', label: 'No preferences', hint: 'Cannot be matched to a child preference' },
  { key: 'noPack', label: 'Not in a pack', hint: 'Not in any physical deck' },
  { key: 'noPlayType', label: 'No play type', hint: 'No play type tagged' },
  {
    key: 'noPrimarySkill',
    label: 'Only borrowed skills',
    hint: 'No skill from the activity’s own primary domain',
  },
];

interface CatalogueHealthStripProps {
  health: ActivityOverviewHealth;
  noActiveDomainCount: number;
}

function HealthCard({ label, hint, count, total }: { label: string; hint: string; count: number; total: number }) {
  const hasGap = count > 0;
  return (
    <div
      title={hint}
      className={cn(
        'rounded-pine-card border px-3 py-2',
        hasGap ? 'border-amber-300 bg-amber-50' : 'border-pine-line bg-pine-card'
      )}
    >
      <p className={cn('text-caption', hasGap ? 'text-amber-800 font-medium' : 'text-pine-ink-soft')}>
        {label}
      </p>
      <p className="mt-0.5 flex items-baseline gap-2 tabular-nums">
        <span className={cn('text-lg font-semibold', hasGap ? 'text-amber-900' : 'text-pine-approved')}>
          {hasGap ? count : '✓ 0'}
        </span>
        {total > 0 && (
          <span className={cn('text-caption', hasGap ? 'text-amber-700' : 'text-pine-ink-soft')}>
            {Math.round((count / total) * 100)}%
          </span>
        )}
      </p>
    </div>
  );
}

/** Content gaps under the active status filter, as counts and share of `health.total`. */
export function CatalogueHealthStrip({ health, noActiveDomainCount }: CatalogueHealthStripProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {HEALTH_ITEMS.map((item) => (
        <HealthCard
          key={item.key}
          label={item.label}
          hint={item.hint}
          count={health[item.key]}
          total={health.total}
        />
      ))}
      {noActiveDomainCount > 0 && (
        <HealthCard
          label="No active domain"
          hint="Primary domain is missing or deleted"
          count={noActiveDomainCount}
          total={health.total}
        />
      )}
    </div>
  );
}
